import { STANDARD, DELUXE, CORE, QUANTUM } from 'core/constants/products'
import isRXPlan from 'utils/pivot/plan/isRXPlan'
import { toString } from 'utils/pivot/product'
import isNumber from 'utils/number/isNumber'
import getMin from 'utils/number/getMin'
import getMax from 'utils/number/getMax'
import { getBenefits } from 'core/controllerPlans/getBenefits'
import { IS_ENROLL } from 'core/constants/environment'
import optionFiltersByTypeBuilder from './optionFiltersByTypeBuilder'
import getPlanTypeKey from 'core/controllerPlans/getPlanTypeKey'
import store from 'utils/store/withMiddleware'
import { propAccessor, IDictionary } from '@hc-frontend/utils'
import ascSort from 'utils/ascSort'

function getOnlyTwoDurations(durations, state_acronym) {
  const maxByPolicyDuration = {}
  const result = durations
    .map(duration => {
      if (
        !maxByPolicyDuration[duration.policyDuration] ||
        duration.recurrence > maxByPolicyDuration[duration.policyDuration]
      ) {
        maxByPolicyDuration[duration.policyDuration] = duration.recurrence
      }

      return duration
    })
    .filter(
      duration =>
        (IS_ENROLL && duration.policyDuration === 364) ||
        duration.recurrence === 1 ||
        duration.recurrence === maxByPolicyDuration[duration.policyDuration]
    )

  return result
}

function hasPrescriptionDrug(plan) {
  if (
    plan.productId === STANDARD ||
    plan.productId === DELUXE ||
    isRXPlan(plan) ||
    (plan.productId === CORE && plan.coreBenefits.deductible === '2000') ||
    (plan.productId === QUANTUM && plan.coreBenefits.quantumType == 'copay')
  ) {
    return 'yes'
  }

  return 'no'
}

function setMinMax(acc, value) {
  value = +value

  if (isNumber(value)) {
    acc.min = getMin(acc.min, value)
    acc.max = getMax(acc.max, value)
  }
}

function getSTMPlansAdditionalData(
  stmIdPlans: number[],
  plans,
  brochures = {}
): STMPlansAdditionalData {
  const benefits = new Set<string>()
  const type = new Set<string>()
  const period = new Set<number>()
  const networkProvider = new Set<string>()
  const coinsurance = new Set<string>()
  const moop = new Set<string>()
  const drug = new Set<string>()
  const deductibleOptions = new Set<number>()
  const coverageOptions = new Set<number>()
  const coverage: FilterRange = {
    min: Infinity,
    max: -Infinity
  }
  const deductible: FilterRange = {
    min: Infinity,
    max: -Infinity
  }
  const policies = {}
  const brochuresByProductType = {}
  const filterOptionsByType = optionFiltersByTypeBuilder()
  const state = store.getState()
  const state_acronym = propAccessor(
    state as unknown as IDictionary<unknown>,
    'hcStorage.location.state_acronym'
  )

  for (const id of stmIdPlans) {
    const plan = plans[id] ?? plans[String(id)]
    if (!plan) continue
    plan.type = toString(plan.productId)
    const planType = getPlanTypeKey(plan)
    const brochure = brochures[plan.brochureId]
    brochuresByProductType[plan.productId] = brochure
    const coreBenefits = plan.coreBenefits
    networkProvider.add(coreBenefits.networkProvider)
    coinsurance.add(coreBenefits.userCoinsurance)
    filterOptionsByType.add(planType, 'coinsurance', coreBenefits.userCoinsurance)

    moop.add(coreBenefits.outOfPocketMax)
    filterOptionsByType.add(planType, 'moop', +coreBenefits.outOfPocketMax)
    plan.drug = hasPrescriptionDrug(plan)
    drug.add(plan.drug)
    type.add(plan.type)
    deductibleOptions.add(+coreBenefits.deductible)
    filterOptionsByType.add(planType, 'deductibleOptions', +coreBenefits.deductible)
    coverageOptions.add(+coreBenefits.lifetimeMax)
    filterOptionsByType.add(planType, 'coverageOptions', +coreBenefits.lifetimeMax)
    setMinMax(coverage, coreBenefits.lifetimeMax)
    setMinMax(deductible, coreBenefits.deductible)
    getBenefits(plan).forEach(benefits.add, benefits)

    const durations = {}
    let durationsArr: Array<{
      policyDuration: number
      recurrence: number
      sequence?: number
      extendedDurationsAvailable?: number[]
    }> = Array.isArray(plan.durationsArray)
      ? plan.durationsArray
      : Array.isArray(plan.durations)
      ? plan.durations
      : []

    if (
      !durationsArr.length &&
      plan.durations &&
      typeof plan.durations === 'object' &&
      !Array.isArray(plan.durations)
    ) {
      const obj = plan.durations as Record<
        number,
        Record<
          number,
          {
            policyDuration: number
            recurrence: number
            sequence?: number
            extendedDurationsAvailable?: number[]
          }
        >
      >
      durationsArr = Object.keys(obj).flatMap(pd =>
        Object.keys(obj[+pd] || {})
          .map(r => obj[+pd][+r])
          .filter(Boolean)
      )
    }

    const maxTwoDuration =
      getOnlyTwoDurations(durationsArr, state_acronym)?.filter(d => d.sequence === 1) ||
      []

    for (const duration of maxTwoDuration) {
      period.add(duration.policyDuration)
      filterOptionsByType.add(
        planType,
        'period',
        `${duration.recurrence > 1 ? `${duration.recurrence}x` : ''}${
          duration.policyDuration
        }`
      )
      durations[duration.policyDuration] = durations[duration.policyDuration] || {}
      durations[duration.policyDuration][duration.recurrence] = duration

      if (policies[duration.policyDuration]) {
        policies[duration.policyDuration].max = Math.max(
          policies[duration.policyDuration].max,
          duration.recurrence
        )
        policies[duration.policyDuration].min = Math.min(
          policies[duration.policyDuration].min,
          duration.recurrence
        )
        policies[duration.policyDuration][duration.recurrence] = true
        if (duration.extendedDurationsAvailable?.[0]) {
          policies[duration.policyDuration].extendedDuration =
            duration.extendedDurationsAvailable[0]
        }
      } else {
        policies[duration.policyDuration] = {
          [duration.recurrence]: true,
          max: duration.recurrence,
          min: duration.recurrence,
          ...(duration.extendedDurationsAvailable?.[0] && {
            extendedDuration: duration.extendedDurationsAvailable[0]
          })
        }
      }
    }

    // convert duration to object
    // where period and recurrence are the keys
    // durations = {
    //   180: {
    //     1: {},
    //     2: {}
    //   }
    // }

    if (!plan.durationsArray) {
      plan.durationsArray = plan.durations
    }

    plan.durations = durations
  }

  // fix noUiSlider when values are equals
  if (coverage.min === coverage.max) {
    coverage.max = coverage.min
    coverage.min = 0
    coverage.disabled = true
  }

  // fix noUiSlider when values are equals
  if (deductible.min === deductible.max) {
    deductible.max = deductible.min
    deductible.min = 0
    deductible.disabled = true
  }

  return {
    brochuresByProductType,
    filterOptionsByProductType: filterOptionsByType.getFormatted(),
    filterOptions: {
      benefits: {
        type: 'multiple',
        values: [...benefits]
      },
      type: {
        type: 'multiple',
        values: [...type]
      },
      price: {
        type: 'multiple-range',
        values: [
          { min: 0, max: 100 },
          { min: 101, max: 200 },
          { min: 201, max: 300 },
          { min: 301, max: 400 },
          { min: 401, max: Infinity }
        ]
      },
      period: {
        type: 'one',
        values: [...period],
        policies
      },
      coverage: {
        type: 'range',
        values: coverage,
        options: [...coverageOptions].sort(ascSort)
      },
      networkProvider: {
        type: 'multiple',
        values: [...networkProvider]
      },
      deductible: {
        type: 'range',
        values: deductible,
        options: [...deductibleOptions].sort(ascSort)
      },
      coinsurance: {
        type: 'multiple',
        values: [...coinsurance].sort()
      },
      moop: {
        type: 'multiple',
        values: [...moop].sort((a, b) => +a - +b)
      },
      drug: {
        type: 'multiple',
        values: [...drug]
      }
    }
  }
}

export default getSTMPlansAdditionalData
